import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Minimal EN stub resolver for SKU i18n keys — the FALLBACK path only.
 *
 * Converts a dot-separated key into a human-readable English label.
 * Examples:
 *   "sku.moon_phase_soulmate.name"  → "Moon Phase Soulmate"
 *   "sku.past_lives.desc"           → "Past Lives"
 *
 * Real localized names/descriptions now live under the `sku.<code>.{name,desc}`
 * keys in the `common` i18n namespace (en/ru/uk). Prefer {@link useSkuLabel},
 * which looks those up and only falls back to this stub for an untranslated key
 * — so a missing entry degrades to "Avatar Cinematic" rather than a raw key.
 */
export function resolveSkuLabel(key: string): string {
  // key format: "sku.{code}.{field}" — extract the {code} segment
  const parts = key.split('.');
  // If there are at least 2 segments, use the second one (the code); else use the whole key
  const codePart = parts.length >= 2 ? parts[1] : parts[0];
  return codePart
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Hook returning a resolver for SKU i18n keys (`sku.<code>.name` / `.desc`).
 *
 * Looks the key up in the `common` namespace and falls back to
 * {@link resolveSkuLabel} when there is no translation, so an untranslated SKU
 * shows a readable label instead of a raw key. Use this everywhere a SKU name
 * or description is rendered (catalog cards, SKU detail, teasers).
 */
export function useSkuLabel(): (key: string) => string {
  const { t } = useTranslation('common');
  // Memoize so the returned resolver is referentially stable (t only changes on
  // language switch) — callers can safely use it in useMemo/useEffect deps.
  return useMemo(() => (key: string): string => t(key, { defaultValue: resolveSkuLabel(key) }), [t]);
}
